// @ts-nocheck
// Read-only list of registered users with their reading progress.
// Pulls profiles + per-user progress aggregates.
//
// Per-row "מחק" button calls admin_delete_user (migration 0017) which
// unclaims any invitations they had, purges purchases, and deletes the
// auth.users row (cascading to profile + all profile-cascade tables).
// Admin users are protected — the RPC refuses to delete them.
//
// Progress column semantics:
//   - The `progress` table has one row per (user_id, chapter), upserted
//     whenever the reader writes in that chapter's textareas (and, after
//     the audio-resume PR, whenever they listen). It does NOT track
//     passive reading.
//   - The column previously showed "the chapter whose row was last
//     upserted" which produced misleading results: a reader who read
//     chapters 2-7 silently after writing in chapter 1 displayed as
//     "פרק 1" because chapter 1 was their last writing-update.
//   - We now show two real signals: MAX(chapter) for furthest reached,
//     and COUNT(distinct chapter) for breadth of engagement. Together
//     they give a useful picture: "פרק 8 · 5 פרקים" means they've been
//     as far as 8 and engaged with 5 different chapters.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useTableSort, SortableTH } from "./useTableSort";

// Roles ranked so sort puts admins → purchasers → invited → registered-only.
// Used as the sort value for the "תפקיד" column.
function roleRank(isAdmin: boolean, isPurchaser: boolean, isInvited: boolean): number {
  if (isAdmin) return 4;
  if (isPurchaser) return 3;
  if (isInvited) return 2;
  return 1;
}

// Format the last-activity timestamp into a short, Shlomo-friendly relative
// string. The exact date is in the cell's `title` tooltip; this is just
// the at-a-glance value:
//   - null/undefined → "טרם נכנס"  (no sign-in AND no progress row ever)
//   - within 24h     → "היום"
//   - within 48h     → "אתמול"
//   - up to 30 days  → "לפני N ימים"
//   - older          → Israeli-format date "DD/MM/YYYY"
//
// Why "activity" not "sign-in": Supabase's auth.users.last_sign_in_at only
// updates on a fresh authentication event (clicking a magic link). Once a
// reader is signed in, silent JWT refreshes keep them logged in for weeks
// without bumping that timestamp — so a reader who's been on the site
// every day for a month might show a stale 30-day-old sign-in. Useless.
// We instead take MAX(last_sign_in_at, MAX(progress.last_read_at)) — the
// progress table is upserted whenever the reader writes, listens, or
// resumes audio, so it's a much truer "is this person still active" signal.
function formatLastActivity(iso: string | null | undefined): string {
  if (!iso) return "טרם נכנס";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diffMs = Date.now() - then;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 1) return "היום";
  if (diffDays === 1) return "אתמול";
  if (diffDays <= 30) return `לפני ${diffDays} ימים`;
  return new Date(iso).toLocaleDateString("he-IL");
}

// Pick the later of two ISO timestamps (either of which may be null/undefined).
// Returns null only when both inputs are missing.
function pickLater(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a && !b) return null;
  if (!a) return b || null;
  if (!b) return a;
  return a > b ? a : b;
}

type Profile = {
  id: string;
  email: string;
  display_name: string | null;
  locale: string;
  created_at: string;
  is_admin: boolean;
};

type ProgressRow = { user_id: string; chapter: number; last_read_at: string };
type InvitationRow = { claimed_by_user_id: string | null; name: string };
type PurchaseRow = { user_id: string; raw_payload: any };

// Per-user aggregate computed client-side from the progress rows.
type ProgressSummary = {
  maxChapter: number;       // furthest chapter reached
  chaptersTouched: number;  // count of distinct chapters with a row
  mostRecent: string;       // ISO timestamp of the latest activity in any chapter
};

// Per-user role + name lookup. Drives the "תפקיד" column and the
// `name` fallback on the שם column.
//
// Role precedence (most specific wins):
//   - admin   → profile.is_admin
//   - רכש     → at least one row in `purchases` with user_id = profile.id
//   - מוזמן   → at least one row in `reviewer_invitations` with claimed_by_user_id = profile.id
//   - —       → registered via magic-link but neither invited nor purchased
//
// Name precedence (first non-empty wins):
//   - profile.display_name (manually set or set by future invitation/webhook sync)
//   - reviewer_invitations.name (when claimed) — populated at invitation creation time
//   - raw_payload.data.attributes.user_name from a Lemon Squeezy purchase
//     (the LS webhook captures this in the JSONB blob even when it doesn't
//     copy to profiles.display_name)
type RoleNameInfo = {
  isPurchaser: boolean;
  isInvited: boolean;
  invitationName: string | null;
  purchaseName: string | null;
};

export function AdminUsers() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [progressByUser, setProgressByUser] = useState<Record<string, ProgressSummary>>({});
  const [roleInfoByUser, setRoleInfoByUser] = useState<Record<string, RoleNameInfo>>({});
  // user_id → ISO timestamp of last sign-in, or null if they've never
  // confirmed/logged in (profile auto-created by trigger but auth.users
  // last_sign_in_at stays null). Fetched via admin_user_last_signins RPC.
  const [lastSigninByUser, setLastSigninByUser] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const profRes = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (profRes.error) {
        setError(profRes.error.message);
        setLoading(false);
        return;
      }
      const profs = profRes.data || [];
      setProfiles(profs);

      // Pull every progress row and aggregate per user. Each row in the
      // progress table is unique on (user_id, chapter) thanks to the
      // table's primary key, so counting rows = counting distinct
      // chapters the reader has touched. Furthest chapter = MAX(chapter).
      // Most recent activity = MAX(last_read_at). All three computed in a
      // single client-side pass — fine for the volumes we expect, no need
      // to push this to an RPC yet.
      const progRes = await supabase
        .from("progress")
        .select("user_id, chapter, last_read_at");
      if (cancelled) return;
      const byUser: Record<string, ProgressSummary> = {};
      for (const r of (progRes.data || []) as ProgressRow[]) {
        const existing = byUser[r.user_id];
        if (!existing) {
          byUser[r.user_id] = {
            maxChapter: r.chapter,
            chaptersTouched: 1,
            mostRecent: r.last_read_at,
          };
        } else {
          if (r.chapter > existing.maxChapter) existing.maxChapter = r.chapter;
          if (r.last_read_at > existing.mostRecent) existing.mostRecent = r.last_read_at;
          existing.chaptersTouched += 1;
        }
      }
      setProgressByUser(byUser);

      // ---------- Roles + name fallbacks + last sign-in ----------
      // Admin RLS lets us select directly from both reviewer_invitations
      // and purchases; last_sign_in_at lives in auth.users and goes through
      // the admin_user_last_signins RPC (migration 0021). All three fire
      // in parallel since they're independent.
      const [invRes, purRes, signinRes] = await Promise.all([
        supabase
          .from("reviewer_invitations")
          .select("claimed_by_user_id, name")
          .not("claimed_by_user_id", "is", null),
        supabase
          .from("purchases")
          .select("user_id, raw_payload"),
        supabase.rpc("admin_user_last_signins"),
      ]);
      if (cancelled) return;

      const signinMap: Record<string, string | null> = {};
      for (const row of (signinRes.data || []) as Array<{ user_id: string; last_sign_in_at: string | null }>) {
        if (row.user_id) signinMap[row.user_id] = row.last_sign_in_at;
      }
      setLastSigninByUser(signinMap);

      const roleInfo: Record<string, RoleNameInfo> = {};
      const ensure = (uid: string): RoleNameInfo => {
        if (!roleInfo[uid]) {
          roleInfo[uid] = {
            isPurchaser: false,
            isInvited: false,
            invitationName: null,
            purchaseName: null,
          };
        }
        return roleInfo[uid];
      };

      for (const inv of (invRes.data || []) as InvitationRow[]) {
        if (!inv.claimed_by_user_id) continue;
        const info = ensure(inv.claimed_by_user_id);
        info.isInvited = true;
        // First non-empty name wins. The invitations table stores the
        // friendly name we entered when sending the invite.
        if (!info.invitationName && inv.name && inv.name.trim() !== "") {
          info.invitationName = inv.name.trim();
        }
      }

      for (const pur of (purRes.data || []) as PurchaseRow[]) {
        if (!pur.user_id) continue;
        const info = ensure(pur.user_id);
        info.isPurchaser = true;
        // Lemon Squeezy puts the buyer name at
        //   data.attributes.user_name
        // inside the raw webhook payload. Manual-grant purchases store a
        // different shape (`{granted_by, note}`) so we defensively check.
        if (!info.purchaseName) {
          const name = pur.raw_payload?.data?.attributes?.user_name;
          if (typeof name === "string" && name.trim() !== "") {
            info.purchaseName = name.trim();
          }
        }
      }
      setRoleInfoByUser(roleInfo);

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter(
      (p) =>
        p.email.toLowerCase().includes(q) ||
        (p.display_name || "").toLowerCase().includes(q)
    );
  }, [profiles, query]);

  // Resolve the displayed name once per row so the comparator below sorts
  // on what the user actually sees in the "שם" column (display_name fallback
  // to invitation name fallback to LS purchase name).
  const resolvedNameOf = (p: Profile): string => {
    const ri = roleInfoByUser[p.id];
    return (
      (p.display_name && p.display_name.trim()) ||
      ri?.invitationName ||
      ri?.purchaseName ||
      ""
    );
  };

  const { sortBy, sortIndicator, sortedRows } = useTableSort(filtered, {
    email:        (p) => p.email,
    name:         (p) => resolvedNameOf(p),
    created_at:   (p) => p.created_at,
    last_activity: (p) => pickLater(lastSigninByUser[p.id], progressByUser[p.id]?.mostRecent),
    progress:     (p) => progressByUser[p.id]?.maxChapter ?? 0,
    role:         (p) => roleRank(p.is_admin, !!roleInfoByUser[p.id]?.isPurchaser, !!roleInfoByUser[p.id]?.isInvited),
  });

  return (
    <>
      <div className="adm-toolbar">
        <input
          className="adm-search"
          placeholder="חיפוש לפי שם או מייל…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {error && <div className="adm-error">{error}</div>}

      <div className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="adm-loading">טוען משתמשים…</div>
        ) : filtered.length === 0 ? (
          <div className="adm-empty">אין משתמשים תואמים.</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <SortableTH k="email" sortBy={sortBy} sortIndicator={sortIndicator}>מייל</SortableTH>
                <SortableTH k="name" sortBy={sortBy} sortIndicator={sortIndicator}>שם</SortableTH>
                <SortableTH k="created_at" sortBy={sortBy} sortIndicator={sortIndicator}>נרשם</SortableTH>
                <SortableTH
                  k="last_activity"
                  sortBy={sortBy}
                  sortIndicator={sortIndicator}
                  title="הפעם האחרונה שראינו פעילות של המשתמש — כניסה רעננה (לחיצה על קישור קסם) או כתיבה/האזנה/קריאת פרק. ׳טרם נכנס׳ = ההזמנה התקבלה אבל המשתמש לא לחץ על הקישור ולא נגע באתר."
                >
                  פעילות אחרונה
                </SortableTH>
                <SortableTH
                  k="progress"
                  sortBy={sortBy}
                  sortIndicator={sortIndicator}
                  title="פרק רחוק ביותר שהגיע אליו · מספר פרקים בהם כתב או האזין"
                >
                  התקדמות
                </SortableTH>
                <SortableTH k="role" sortBy={sortBy} sortIndicator={sortIndicator}>תפקיד</SortableTH>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((p) => {
                const prog = progressByUser[p.id];
                const ri = roleInfoByUser[p.id];
                // Name fallback chain. profile.display_name first
                // (manually-set or future invitation/webhook sync),
                // then the invitation's name (set at invite time),
                // then the LS payload's user_name (set by buyer at
                // checkout). "—" if nothing available.
                const resolvedName =
                  (p.display_name && p.display_name.trim()) ||
                  ri?.invitationName ||
                  ri?.purchaseName ||
                  "—";
                return (
                  <tr key={p.id}>
                    <td className="mono">{p.email}</td>
                    <td>{resolvedName}</td>
                    <td className="num">
                      {new Date(p.created_at).toLocaleDateString("he-IL")}
                    </td>
                    {/* Latest activity = later of (last fresh sign-in, last
                        progress upsert). Either alone is misleading:
                        last_sign_in_at lags daily readers who never re-auth,
                        and progress only fires on writing/listening — not on
                        passive reading of a single chapter without progress
                        write. The combined max is the truest signal we have
                        without adding new tracking. */}
                    {(() => {
                      const activity = pickLater(lastSigninByUser[p.id], prog?.mostRecent);
                      return (
                        <td
                          className="num"
                          title={
                            activity
                              ? new Date(activity).toLocaleString("he-IL")
                              : "המשתמש לא נכנס לאתר ולא נגע באתר אף פעם — ההזמנה התקבלה אבל הוא לא לחץ על הקישור במייל"
                          }
                        >
                          {formatLastActivity(activity)}
                        </td>
                      );
                    })()}
                    <td
                      className="num"
                      title={
                        prog
                          ? `פעילות אחרונה: ${new Date(prog.mostRecent).toLocaleDateString("he-IL")}`
                          : undefined
                      }
                    >
                      {prog
                        ? `פרק ${prog.maxChapter} · ${prog.chaptersTouched} מתוך 12`
                        : "—"}
                    </td>
                    <td>
                      {/* Role precedence: admin > purchased > invited.
                          Pill styles come from the existing admin CSS —
                          .ok green for admin, .accent (warm orange) for
                          paying customers, .dim sand for invited
                          reviewers, plain neutral for everyone else. */}
                      {p.is_admin ? (
                        <span className="adm-pill ok">אדמין</span>
                      ) : ri?.isPurchaser ? (
                        <span className="adm-pill accent">רכש</span>
                      ) : ri?.isInvited ? (
                        <span className="adm-pill dim">מוזמן</span>
                      ) : (
                        <span className="adm-pill">—</span>
                      )}
                    </td>
                    <td>
                      <DeleteUserButton
                        profile={p}
                        onDeleted={() => setRefresh((n) => n + 1)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function DeleteUserButton({
  profile,
  onDeleted,
}: {
  profile: Profile;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);

  if (profile.is_admin) {
    return <span style={{ color: "#8B7A66", fontSize: 12 }}>—</span>;
  }

  const onClick = async () => {
    const confirmed = window.confirm(
      `למחוק לצמיתות את המשתמש ${profile.display_name || profile.email}?\n\n` +
        `הפעולה תמחק את החשבון, הגישות, ההתקדמות, וההיסטוריה הכספית של המשתמש, ` +
        `ותאפס הזמנות שהוא ניצל כך שאפשר להזמין מחדש עם אותו מייל.\n\nלא ניתן לשחזר.`
    );
    if (!confirmed) return;

    setBusy(true);
    const { error } = await supabase.rpc("admin_delete_user", {
      _user_id: profile.id,
    });
    setBusy(false);

    if (error) {
      window.alert("שגיאה במחיקה: " + error.message);
      return;
    }
    onDeleted();
  };

  return (
    <button
      className="adm-btn secondary tiny"
      onClick={onClick}
      disabled={busy}
      title="מחיקה לצמיתות של המשתמש"
    >
      {busy ? "מוחק…" : "מחק"}
    </button>
  );
}
